/*
  Console Sanitizer & Guard
  - Auto-clears console on interval and navigation
  - Redacts sensitive values
  - Silences noisy logs (with optional allowlist)
  - Installs global error/rejection handlers
*/

const DEFAULT_SENSITIVE_PATTERNS = [
  /apikey=([A-Za-z0-9\._-]+)/gi,
  /api_key=([A-Za-z0-9\._-]+)/gi,
  /authorization:\s*bearer\s+[A-Za-z0-9\._-]+/gi,
  /eyJ[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9_-]{10,}/g, // JWT-like
  /supabase\.[a-z]+\.[a-z]+\/[\w\-\/\?=.&]+/gi,
  /password\s*[:=]\s*[^\s]+/gi,
  /token\s*[:=]\s*[^\s]+/gi,
]

function redact(message, patterns) {
  let text = String(message)
  for (const re of patterns) {
    text = text.replace(re, match => {
      const cleaned = match.slice(0, Math.min(6, match.length))
      return `${cleaned}***REDACTED***`
    })
  }
  return text
}

function stringifyArgs(args, patterns) {
  try {
    return args.map(a => {
      if (a == null) return a
      if (typeof a === 'string') return redact(a, patterns)
      if (a instanceof Error) return redact(`${a.name}: ${a.message}`, patterns)
      // Safe stringify objects
      return redact(JSON.stringify(a, (_k, v) => (typeof v === 'function' ? '[Function]' : v)), patterns)
    })
  } catch (_e) {
    return ['[Unserializable log arguments]']
  }
}

function installNavigationClear() {
  const clear = () => {
    try { console.clear() } catch (_) {}
  }
  try {
    const push = history.pushState
    const replace = history.replaceState
    history.pushState = function () { clear(); return push.apply(this, arguments) }
    history.replaceState = function () { clear(); return replace.apply(this, arguments) }
  } catch (_) {}
  window.addEventListener('popstate', () => {
    clear()
  })
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') clear()
  })
  window.addEventListener('focus', clear)
}

export function installConsoleGuards(config = {}) {
  const {
    environment = 'production',
    autoClearIntervalMs = 15000,
    clearOnNavigation = true,
    sensitivePatterns = DEFAULT_SENSITIVE_PATTERNS,
    allowlistNamespaces = [], // e.g. ['app:important']
    suppressLevels = ['log', 'info', 'debug', 'warn'],
  } = config

  // Auto clear on interval
  let intervalId = null
  try {
    console.clear()
    intervalId = setInterval(() => {
      try { console.clear() } catch (_) {}
    }, autoClearIntervalMs)
  } catch (_) {}

  if (clearOnNavigation) installNavigationClear()

  // Namespace allowlist helper: console.log('app:ns', ...)
  const isAllowed = (args) => {
    if (!allowlistNamespaces.length) return false
    if (!args || !args.length) return false
    const first = typeof args[0] === 'string' ? args[0] : ''
    return allowlistNamespaces.some(ns => first.startsWith(ns))
  }

  // Wrap console methods
  const original = {
    log: console.log,
    info: console.info,
    debug: console.debug,
    warn: console.warn,
    error: console.error,
  }

  const wrap = (level) => function wrappedConsoleMethod(...args) {
    try {
      // Always redact
      const safeArgs = stringifyArgs(args, sensitivePatterns)
      // In production, suppress noisy levels unless allowlisted
      const isNoisy = suppressLevels.includes(level)
      const inProd = environment === 'production'
      if (inProd && isNoisy && !isAllowed(args)) {
        return // drop
      }
      // In dev, still print but redacted
      return original[level].apply(console, safeArgs)
    } catch (_) {
      // If something goes wrong, do not break app
      try { return original[level].apply(console, args) } catch (_) {}
    }
  }

  console.log = wrap('log')
  console.info = wrap('info')
  console.debug = wrap('debug')
  console.warn = wrap('warn')

  // Always keep errors but redact
  console.error = function (...args) {
    const safeArgs = stringifyArgs(args, sensitivePatterns)
    try { return original.error.apply(console, safeArgs) } catch (_) {}
  }

  // Global error handlers
  const capture = []
  Object.defineProperty(window, '__capturedErrors__', {
    get() { return capture },
  })

  window.addEventListener('error', (e) => {
    const payload = {
      type: 'error',
      message: redact(e?.error?.message || e.message || 'Unknown error', sensitivePatterns),
      source: e.filename,
      line: e.lineno,
      col: e.colno,
      time: Date.now(),
    }
    capture.push(payload)
    // Do not let the error spam the console repeatedly
  })

  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason instanceof Error ? `${e.reason.name}: ${e.reason.message}` : String(e.reason)
    const payload = {
      type: 'unhandledrejection',
      message: redact(reason, sensitivePatterns),
      time: Date.now(),
    }
    capture.push(payload)
  })

  // Return an uninstall handle
  return () => {
    if (intervalId) clearInterval(intervalId)
    console.log = original.log
    console.info = original.info
    console.debug = original.debug
    console.warn = original.warn
    console.error = original.error
  }
}


